import React, { useEffect, useState } from "react";
import {
  AlarmChannelContainer,
  AlarmChannelLabelContainer,
  Container,
  GWSMemberSyncContainer,
  GWSMemberSyncInfoContainer,
  LabelContainer,
  LeftContentContainer,
  MemberInfoSyncChannel,
  MemberInfoSyncContainer,
  MemberSyncInfoGrayContainer,
  SyncBtnContainer,
} from "./GoogleSyncInfo.styles";
import UI from "@mile-meeting/mile-ui";
import ToolTip from "uis/new-tootip";
import { TMember } from "types/user.types";
import { useRecoilValue } from "recoil";
import { member } from "recoil/recoilState";
import { TIntegrationCategory } from "apis/member-integration";
import { AlarmChannel } from "apis/alarm-settings";
import { useToast } from "hook/useToast";
import { getMessengers } from "apis/messangers";

type GoogleSyncInfoProps = {
  domainList?: string[];
  orgUnitList?: string[];
  syncedCreatedNum?: number;
  syncedDeletedNum?: number;
  lastSyncedDate?: string;
  integratedById?: TMember;
  integrationCategory?: TIntegrationCategory;
  alarmChannel?: AlarmChannel;
  onHandleUnlink?: () => void;
  onHandleSync?: () => void;
  onHandleAlarmChannelChange?: (channel: AlarmChannel) => void;
  onHandleSyncInfoChange?: () => void;
};
const GoogleSyncInfo: React.FC<GoogleSyncInfoProps> = ({
  domainList,
  orgUnitList,
  syncedCreatedNum,
  syncedDeletedNum,
  lastSyncedDate,
  integratedById,
  integrationCategory,
  alarmChannel,
  onHandleUnlink,
  onHandleSync,
  onHandleAlarmChannelChange,
  onHandleSyncInfoChange,
}) => {
  const memberInfo = useRecoilValue(member);
  const enabledChangeChannel = memberInfo && integratedById && memberInfo.id === integratedById.id;
  const [enableSlack, setEnableSlack] = useState(false);
  const { showToast } = useToast();

  const onClickAlarmChannel = (channel: AlarmChannel) => {
    if (!enableSlack && channel === AlarmChannel.SLACK && enabledChangeChannel) {
      showToast("Slack 미연동 상태입니다. 서비스 연동 관리에서 Slack을 연동해 주세요.", "warning");
      return;
    }
    if (enabledChangeChannel) {
      onHandleAlarmChannelChange?.(channel);
    }
  };

  useEffect(() => {
    getMessengers().then((res) => {
      if (res.success) {
        const slack = res.data?.some((item) => item?.messengerProvider === "SLACK");
        if (slack) {
          setEnableSlack(true);
        }
      }
    });
  }, []);

  return (
    <>
      <Container>
        <GWSMemberSyncContainer>
          <LeftContentContainer>
            <UI.Typography type="body01 normal" fontStyle="bold">
              GWS 멤버 정보 연동
            </UI.Typography>
            <UI.Typography type="body02 normal" color="var(--alias-color-label-alternative)">
              Google Workspace 내 도메인 혹은 조직 단위의 연동 정보 변경 및 연동 해제가 가능합니다.
            </UI.Typography>
          </LeftContentContainer>
          <SyncBtnContainer>
            <UI.Button
              type="outlined"
              size="small"
              title="연동 해제하기"
              prefixIcon={"unLink"}
              showPrefixIcon
              onClick={onHandleUnlink}
            />
            <UI.Button
              size="small"
              title="연동 정보 변경하기"
              prefixIcon={"modify"}
              showPrefixIcon
              onClick={onHandleSyncInfoChange}
            />
          </SyncBtnContainer>
        </GWSMemberSyncContainer>
        <GWSMemberSyncInfoContainer>
          <LabelContainer>
            <UI.Typography type="body01 normal" color="var(--alias-color-label-normal)">
              연동 정보
            </UI.Typography>
            <UI.Chip.Contained
              size="small"
              title={integrationCategory === "DOMAIN" ? "도메인" : "조직"}
              type="secondary"
            />
          </LabelContainer>
          <MemberSyncInfoGrayContainer>
            <UI.Typography
              type="label01"
              fontStyle="regular"
              color="var(--alias-color-label-normal)"
              style={{
                whiteSpace: "pre-line",
              }}
            >
              {integrationCategory === "DOMAIN" && domainList?.length === 0
                ? "연동된 도메인이 없습니다."
                : integrationCategory === "DOMAIN" && domainList?.map((item) => `· ${item}`).join("\n")}
              {integrationCategory === "ORG_UNIT" && orgUnitList?.length === 0
                ? "연동된 조직 단위가 없습니다."
                : integrationCategory === "ORG_UNIT" &&
                  orgUnitList?.map((item) => `· ${item.slice(1).split("/").join(" > ")}`).join("\n")}
            </UI.Typography>
          </MemberSyncInfoGrayContainer>
        </GWSMemberSyncInfoContainer>
        <MemberInfoSyncContainer>
          <LeftContentContainer>
            <UI.Typography type="body01 normal" color="var(--alias-color-label-normal)">
              멤버 정보 동기화
            </UI.Typography>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <UI.Typography type="label01" color="var(--alias-color-label-alternative)">
                연동 대상 도메인/조직에 한해 변동사항 확인 후, 동기화를 진행합니다.
              </UI.Typography>
              {lastSyncedDate && (
                <UI.Typography type="caption01" fontStyle="regular" color="var(--alias-color-label-alternative)">
                  * 마지막 동기화: {lastSyncedDate && lastSyncedDate.split("T")[0].replaceAll("-", ". ")} (신규{" "}
                  {syncedCreatedNum}인, 삭제 {syncedDeletedNum}인)
                </UI.Typography>
              )}
              {!lastSyncedDate && (
                <UI.Typography type="caption01" fontStyle="regular" color="var(--alias-color-label-alternative)">
                  * 마지막 동기화: 없음
                </UI.Typography>
              )}
            </div>
          </LeftContentContainer>
          <UI.Button
            title="동기화하기"
            prefixIcon={"change"}
            size="small"
            showPrefixIcon
            primary={false}
            onClick={onHandleSync}
          />
        </MemberInfoSyncContainer>
      </Container>
      <MemberInfoSyncChannel>
        <LeftContentContainer>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <UI.Typography type="body01 normal" fontStyle="bold" color="var(--alias-color-label-normal)">
              GWS 멤버 정보 변동 알림 설정
            </UI.Typography>
            <ToolTip
              icon={<UI.Icon name="info" size={16} color="var(--alias-color-label-assistive)" />}
              text={
                <p>
                  GWS 멤버 정보에 변동이 생길 경우 이를 감지하여
                  <br />
                  변동사항 확인과 동기화가 가능한 알림을 보내드립니다.
                </p>
              }
              position={{
                top: -52,
                left: -40,
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <UI.Typography type="label01" color="var(--alias-color-label-alternative)">
              알림은 최초 연동을 시행한 사용자에게만 전송되며, 해당 사용자는 알림 채널 설정이 가능합니다.
            </UI.Typography>
            <UI.Typography type="caption01" fontStyle="regular" color="var(--alias-color-label-alternative)">
              * 수신자: {integratedById?.nickname}({integratedById?.email})
            </UI.Typography>
          </div>
        </LeftContentContainer>

        <AlarmChannelContainer>
          <AlarmChannelLabelContainer onClick={() => onClickAlarmChannel(AlarmChannel.EMAIL)}>
            <UI.Radio selected={alarmChannel === AlarmChannel.EMAIL} disabled={!enabledChangeChannel} />
            <UI.Typography
              type="body01 normal"
              fontStyle="regular"
              color={enabledChangeChannel ? "var(--alias-color-label-normal)" : "var(--alias-color-label-disabled)"}
            >
              E-mail
            </UI.Typography>
          </AlarmChannelLabelContainer>
          <AlarmChannelLabelContainer onClick={() => onClickAlarmChannel(AlarmChannel.SLACK)}>
            <UI.Radio selected={alarmChannel === AlarmChannel.SLACK} disabled={!enabledChangeChannel || !enableSlack} />
            <UI.Typography
              type="body01 normal"
              fontStyle="regular"
              color={enabledChangeChannel ? "var(--alias-color-label-normal)" : "var(--alias-color-label-disabled)"}
            >
              Slack
            </UI.Typography>
          </AlarmChannelLabelContainer>
        </AlarmChannelContainer>
      </MemberInfoSyncChannel>
    </>
  );
};
export default React.memo(GoogleSyncInfo);
